/** ============================================================
 * auth.js — Manejo de sesión JWT y seguridad por rol
 * ============================================================ */

const BO_AUTH = {

    // Obtener usuario actual del sessionStorage o localStorage
    getUser() {
        try {
            const raw = sessionStorage.getItem('bo_user') || localStorage.getItem('bo_user');
            if (!raw) return null;
            const u = JSON.parse(raw);
            if (u) {
                const reg = Number(u.registro || u.id || 0);
                const uid = String(u.id || '');
                if (reg === 80000 || uid === '80000' || uid === 'fc5ebbe0-5951-11e8-9132-bd317f0a484f' || u.rol_id === '00000000-0000-0000-0000-000000000001') {
                    if (u.rol !== 'master') {
                        u.rol = 'master';
                        try { sessionStorage.setItem('bo_user', JSON.stringify(u)); } catch (_) {}
                    }
                } else {
                    u.rol = (u.rol || u.rol_global || u.role || u.tipo || '').toLowerCase().replace(/\s+/g, '_').trim();
                }
            }
            return u;
        } catch { return null; }
    },

    getToken() { 
        return sessionStorage.getItem('bo_token') || localStorage.getItem('bo_token'); 
    },

    isTokenExpired() {
        const token = this.getToken();
        if (!token) return true;
        try {
            const parts = token.split('.');
            if (parts.length < 2) return true;
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            
            // 1. Si el JWT define fecha de expiración explícita (exp en segundos)
            if (payload.exp && Date.now() >= payload.exp * 1000) {
                this._cleanLocalTokens();
                return true;
            }

            // 2. Si no tiene exp pero tiene iat, limitar a máximo 12 horas
            const issuedAt = payload.iat ? payload.iat * 1000 : 0;
            if (issuedAt && (Date.now() - issuedAt > 12 * 3600 * 1000)) {
                this._cleanLocalTokens();
                return true;
            }
            return false;
        } catch(e) {
            return false;
        }
    },

    _cleanLocalTokens() {
        try {
            sessionStorage.removeItem('bo_token');
            sessionStorage.removeItem('bo_user');
            localStorage.removeItem('bo_token');
            localStorage.removeItem('bo_user');
        } catch (_) {}
    },

    isAuthenticated() {
        return !!this.getToken() && !!this.getUser() && !this.isTokenExpired();
    },

    // Roles en orden de permiso (de mayor a menor)
    ROLES: ['master', 'admin', 'administrador', 'contabilidad', 'supervisor_almacen', 'almacen', 'operativo', 'cliente', 'prospecto'],

    hasRole(requiredRole) {
        const user = this.getUser();
        if (!user) return false;
        const reg = Number(user.registro || user.id || 0);
        const uid = String(user.id || '');
        if (reg === 80000 || uid === '80000' || uid === 'fc5ebbe0-5951-11e8-9132-bd317f0a484f' || user.rol_id === '00000000-0000-0000-0000-000000000001') {
            return true;
        }
        // Normalize for case/language variants
        const userRol = (user.rol || '').toLowerCase().replace(/ /g, '_').trim();
        // Treat 'administrador' as equivalent to 'admin'
        const normalizedUserRol = userRol === 'administrador' ? 'admin' : userRol;
        const normalizedReqRole = requiredRole === 'administrador' ? 'admin' : requiredRole;
        const userIdx = this.ROLES.indexOf(normalizedUserRol);
        const reqIdx  = this.ROLES.indexOf(normalizedReqRole);
        // Tiene permiso si su rol es igual o mayor (menor índice)
        return userIdx !== -1 && userIdx <= reqIdx;
    },

    _loginUrl() {
        if (window.location.pathname.includes('/pages/') || window.location.hostname.includes('duletbio') || window.location.hostname.includes('myshopify')) {
            return '/pages/login';
        }
        return 'login.html';
    },

    // Guard: redirigir a login si no hay sesión
    requireAuth(minRole = null) {
        if (!this.isAuthenticated()) {
            window.location.href = this._loginUrl();
            return false;
        }
        if (minRole && !this.hasRole(minRole)) {
            window.location.href = window.location.pathname.includes('/pages/') ? '/pages/unauthorized' : 'unauthorized.html';
            return false;
        }
        return true;
    },

    // Guard para acciones de inventario (toggleLiberarLote)
    requireInventoryAdmin() {
        const user = this.getUser();
        const _uid = String(user?.id || '');
        if (!this.hasRole('admin') && !this.hasRole('supervisor_almacen') && _uid !== '101010') {
            window.location.href = window.location.pathname.includes('/pages/') ? '/pages/unauthorized' : 'unauthorized.html';
            return false;
        }
        return true;
    },

    logout() {
        this._cleanLocalTokens();
        try { sessionStorage.clear(); } catch (_) {}
        window.location.href = this._loginUrl();
    },

    // Control de inactividad desactivado: La sesión permanece activa según la vigencia del token JWT (12 horas)
    initIdleTimer() {
        // Desactivado: Sin logout por inactividad
    },

    // Render nombre de rol amigable
    rolLabel(rol) {
        return {
            master:              'Máster',
            admin:               'Administrador',
            administrador:       'Administrador',
            contabilidad:        'Contabilidad',
            supervisor_almacen:  'Supervisor Almacén',
            'supervisor almacen': 'Supervisor Almacén',
            almacen:             'Almacén',
            operativo:           'Operativo CEDIS',
            cliente:             'Empresario',
            empresario:          'Empresario',
            prospecto:           'Prospecto'
        }[(rol || '').toLowerCase().trim()] || rol;
    },

    // Render de estatus de actividad
    estatusLabel(estatus) {
        return {
            nuevo:    '✦ Nuevo',
            blanco:   '● Activo',
            gris:     '● Inactivo',
            amarillo: '⚠ En Riesgo',
            baja:     '✕ Baja'
        }[estatus] || estatus;
    },

    // Menú de navegación por rol
    getNavItems(rol) {
        // Normalize 'administrador' -> 'admin' so sidebar items match
        const _r = (rol || '').toLowerCase().replace(/ /g, '_').trim();
        const normalizedRol = _r === 'administrador' ? 'admin' : _r;
        const all = [
            // ── Principal ─────────────────────────────────────────────
            { id: 'dashboard',            label: 'Panorama general',          icon: '◈',  roles: ['master'] },
            { id: 'dashboard-lider',      label: 'Mi Visión como Empresario', icon: '💎', roles: ['cliente','prospecto'] },
            { id: 'mi-perfil',            label: 'Mi Perfil',                 icon: '👤', roles: ['cliente','prospecto'] },
            { id: 'lideres',              label: 'Líderes',                   icon: '⬡',  roles: ['master','admin','operativo','contabilidad'] },
            { id: 'arbol',               label: 'Cadena',                    icon: '⟁',  roles: ['master','admin','cliente'] },
            { id: 'activos',             label: 'Control Activos',           icon: '◉',  roles: ['master','admin','cliente'] },
            // ── Operaciones ───────────────────────────────────────────
            { id: 'ventas',              label: 'Ventas',                    icon: '⊕',  roles: ['master','admin','operativo','almacen','supervisor_almacen'] },
            { id: 'centros-apoyo',       label: 'Centros de Apoyo',          icon: '🏪', roles: ['master','admin','contabilidad','supervisor_almacen'] },
            { id: 'productos',           label: 'Productos',                 icon: '📦', roles: ['master'] },
            { id: 'auditoria-notas',     label: 'Auditoría de Notas',        icon: '🛡️', roles: ['master'] },
            { id: 'calendario',          label: 'Calendario y Promos',       icon: '📅', roles: ['master','admin'] },
            // ── DI — Producción ───────────────────────────────────────
            { id: 'inventario',          label: 'Inventario CEDIS',          icon: '▣',  roles: ['master','admin','operativo','almacen','supervisor_almacen','contabilidad'] },
            { id: 'produccion',          label: 'Producción',                icon: '🏭', roles: ['master','almacen','supervisor_almacen','contabilidad'] },
            { id: 'proveedores',         label: 'Proveedores',               icon: '🏢', roles: ['master','supervisor_almacen','contabilidad'] },
            // ── Contabilidad ──────────────────────────────────────────
            { id: 'costos',              label: 'Costos y Finanzas',         icon: '💰', roles: ['master','supervisor_almacen','contabilidad'] },
            { id: 'intercompania',       label: 'Intercompañía',             icon: '🔄', roles: ['master','admin','supervisor_almacen','contabilidad'] },
            { id: 'conciliacion',        label: 'Conciliación Bancaria',     icon: '🏦', roles: ['master','admin','supervisor_almacen','contabilidad'] },
            // ── Coaching ──────────────────────────────────────────────
            { id: 'academia',            label: 'Academia Dulet',            icon: '⊞',  roles: ['cliente','prospecto'] },
            { id: 'premios',             label: 'Premios',                   icon: '★',  roles: ['master','admin'] },
            { id: 'academia-admin',      label: 'Academia Dulet',            icon: '🎓', roles: ['master','admin'] },
            { id: 'rescate',             label: 'Zona de Rescate',           icon: '⚑',  roles: ['master','admin'] },
            { id: 'analisis-lider',      label: 'Análisis del Líder',        icon: '📊', roles: ['master','admin'] },
            { id: 'acelerador',          label: 'Mi Proyecto Acelerador',    icon: '🚀', roles: ['master','admin','cliente'] },
            { id: 'bitacora',            label: 'Bitácora del Socio',        icon: '📸', roles: ['master','admin','cliente'] },
            // ── Sistema ───────────────────────────────────────────────
            { id: 'facturacion',         label: 'Facturación',               icon: '≡',  roles: ['master','admin','cliente','contabilidad'] },
            { id: 'facturacion-usa',     label: 'Facturación USA',           icon: '🇺🇸', roles: ['master','contabilidad'] },
            { id: 'config',              label: 'Configuración',             icon: '⚙',  roles: ['master'] }
        ];
        return all.filter(item => {
            if (!item.roles.includes(normalizedRol)) return false;
            // Excepciones individuales por user_id
            const uid = String(BO_AUTH.getUser()?.id || '');
            const HIDDEN_FOR_USER = {
                '61000': ['proveedores']   // Edgar — mismos permisos que almacén (sin Proveedores)
            };
            if (HIDDEN_FOR_USER[uid] && HIDDEN_FOR_USER[uid].includes(item.id)) return false;
            return true;
        });
    },

    // Toast de notificación visual (success | warning | danger | info)
    showAlert(msg, tipo = 'info') {
        const colors = {
            success: { bg: '#052e16', border: '#22C55E', text: '#86efac', icon: '✔' },
            warning: { bg: '#451a03', border: '#F59E0B', text: '#fde68a', icon: '⚠' },
            danger:  { bg: '#450a0a', border: '#EF4444', text: '#fca5a5', icon: '✕' },
            info:    { bg: '#0c1a2e', border: '#3B82F6', text: '#93c5fd', icon: 'ℹ' },
        };
        const s = colors[tipo] || colors.info;
        const toast = document.createElement('div');
        toast.style.cssText = `
            position:fixed;bottom:1.5rem;right:1.5rem;z-index:999999;
            background:${s.bg};border:1px solid ${s.border};color:${s.text};
            padding:.75rem 1.25rem;border-radius:10px;font-size:.85rem;font-weight:600;
            box-shadow:0 8px 24px rgba(0,0,0,.4);display:flex;align-items:center;gap:.6rem;
            max-width:340px;animation:fadeInUp .25s ease;`;
        toast.innerHTML = `<span>${s.icon}</span><span>${msg}</span>`;
        document.body.appendChild(toast);
        setTimeout(() => { toast.style.opacity = '0'; toast.style.transition = 'opacity .3s'; setTimeout(() => toast.remove(), 320); }, 3500);
    }
};

if (typeof window !== 'undefined') {
    window.BO_AUTH = BO_AUTH;
    // Auto-cierre por inactividad desactivado: La sesión se rige por la expiración JWT (12 horas).
}

